import {
    ActionsPanel_AttachAsACopyText,
    ActionsPanel_AttachAsACopyDescriptionText,
    ActionsPanel_MultipleFilesText,
    ActionsPanel_SingleFileText,
    ActionsPanel_InsertPictureInlineText,
    ActionsPanel_InsertPictureInlineTextDescriptionText,
} from './FilePickerActionsPanelView.locstring.json';
import loc from 'owa-localize';
import * as filePickerActionsPanel from '../../actions/actionsPanelActions/showHideFilePickerActionsPanel';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import FilePickerAction from '../../store/schema/FilePickerAction';
import FilePickerActionsPanelButtonView from './FilePickerActionsPanelButtonView';
import FilePickerActionsPanelCheckboxView from './FilePickerActionsPanelCheckboxView';
import FilePickerActionsPanelViewState from '../../store/schema/FilePickerActionsPanelViewState';
import getShareIconForActionPanel from '../utils/getShareIconForActionPanel';

import updateSaveUserChoice from '../../actions/actionsPanelActions/updateSaveUserChoice';
import { AttachOrSharePreference } from 'owa-filepicker';
import { ControlIcons } from 'owa-control-icons';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { IconButton } from 'office-ui-fabric-react/lib/Button';
import { logMountingPerformance } from 'owa-analytics';
import { MessageBar } from 'office-ui-fabric-react/lib/MessageBar';
import { Modal } from 'office-ui-fabric-react/lib/Modal';
import { observer } from 'mobx-react';
import { SelectedFilesSource } from '../../types/FilePickerResult';
import getShareStringsForActionPanel, {
    ShareButtonStrings,
} from '../utils/getShareStringsForActionPanel';
import { FileProviderType } from 'owa-fileprovider-store';

interface FilePickerActionsPanelProps {
    onActionSelected: (action: FilePickerAction) => void;
    onCancelCallback: () => void;
    viewState: FilePickerActionsPanelViewState;
    supportedActions: FilePickerAction[];
    rememberUserChoiceText: string;
    userChoiceSetter: (value: AttachOrSharePreference) => void;
    isMultipleSelection: boolean;
    currentProviderDisplayName: string;
    currentProviderType: FileProviderType;
    supportedActionsMessages?: string[];
    filesSource: SelectedFilesSource;
}

const styles = require('./FilePickerActionsPanelView.scss');

// This component is responsible for showing the share options for the user.
@observer
@logMountingPerformance('FilePickerActionsPanelView')
class FilePickerActionsPanelView extends React.Component<FilePickerActionsPanelProps, {}> {
    private selectedAction: FilePickerAction = null;

    render() {
        return (
            <Modal
                isOpen={this.props.viewState.isOpen}
                onDismiss={this.closePanel}
                onDismissed={this.onPanelDismissed}
                forceFocusInsideTrap={true}>
                <div className={styles.container}>{this.renderFilePickerActionsPanelContent()}</div>
            </Modal>
        );
    }

    private renderFilePickerActionsPanelContent = () => {
        const isAttachSupported = this.props.supportedActions.some(
            action => action === FilePickerAction.Attach
        );
        const isShareSupported = this.props.supportedActions.some(
            action => action === FilePickerAction.Share
        );
        const isInsertSupported = this.props.supportedActions.some(
            action => action === FilePickerAction.Insert
        );
        const isMultipleSelection = this.props.isMultipleSelection;
        const shareButtonStrings: ShareButtonStrings = getShareStringsForActionPanel(
            this.props.filesSource,
            isMultipleSelection,
            this.props.currentProviderDisplayName,
            this.props.viewState.uploadConfiguration
        );
        const shareButtonIcon: JSX.Element = getShareIconForActionPanel(
            this.props.filesSource,
            this.props.currentProviderType,
            !isShareSupported
        );

        const attachButtonComponent = (
            <FilePickerActionsPanelButtonView
                action={FilePickerAction.Attach}
                actionLabel={loc(ActionsPanel_AttachAsACopyText)}
                actionDescription={loc(ActionsPanel_AttachAsACopyDescriptionText)}
                buttonIcon={<Icon iconName={ControlIcons.Attach} className={styles.icon} />}
                isDisabled={!isAttachSupported}
                onActionButtonClicked={this.onActionButtonClicked}
                shouldAutoFocus={isAttachSupported}
            />
        );

        const shareButtonComponent = (
            <FilePickerActionsPanelButtonView
                action={FilePickerAction.Share}
                actionLabel={shareButtonStrings.shareText}
                actionDescription={shareButtonStrings.shareDescription}
                buttonIcon={shareButtonIcon}
                isDisabled={!isShareSupported}
                onActionButtonClicked={this.onActionButtonClicked}
                shouldAutoFocus={!isAttachSupported && isShareSupported}
            />
        );

        const buttons = [shareButtonComponent, attachButtonComponent];

        return (
            <div>
                <div className={styles.title}>
                    {isMultipleSelection
                        ? loc(ActionsPanel_MultipleFilesText)
                        : loc(ActionsPanel_SingleFileText)}
                </div>
                <div className={styles.closeButton}>
                    <IconButton
                        iconProps={{
                            iconName: ControlIcons.Cancel,
                        }}
                        onClick={this.closePanel}
                    />
                </div>
                {this.showActionsPanelMessages()}

                {/* The buttons to render */}
                {buttons}

                {isInsertSupported && (
                    <FilePickerActionsPanelButtonView
                        action={FilePickerAction.Insert}
                        actionLabel={loc(ActionsPanel_InsertPictureInlineText)}
                        actionDescription={loc(ActionsPanel_InsertPictureInlineTextDescriptionText)}
                        buttonIcon={<Icon iconName={ControlIcons.Photo2} className={styles.icon} />}
                        isDisabled={!isAttachSupported}
                        onActionButtonClicked={this.onActionButtonClicked}
                        shouldAutoFocus={false}
                    />
                )}
                {this.props.rememberUserChoiceText && (
                    <FilePickerActionsPanelCheckboxView
                        viewState={this.props.viewState}
                        rememberUserChoiceText={this.props.rememberUserChoiceText}
                    />
                )}
            </div>
        );
    };

    private showActionsPanelMessages = () => {
        return this.props.supportedActionsMessages.map((message, index) => {
            return (
                <MessageBar key={message} className={styles.errorMessage}>
                    {' '}
                    {message}{' '}
                </MessageBar>
            );
        });
    };

    private onActionButtonClicked = (action: FilePickerAction) => {
        this.selectedAction = action;
        this.closePanel();
    };

    private closePanel = () => {
        filePickerActionsPanel.hide(this.props.viewState);
    };

    private onPanelDismissed = () => {
        // We need to make the callbacks only when the animation is complete so the focus can be changed back
        if (this.selectedAction !== null) {
            this.props.onActionSelected(this.selectedAction);
            if (this.props.viewState.saveUserChoice) {
                this.persistUsersPreferredAction(this.selectedAction);
                updateSaveUserChoice(this.props.viewState, false);
            }
            this.selectedAction = null;
        } else {
            this.props.onCancelCallback();
        }
    };

    private persistUsersPreferredAction(action: FilePickerAction) {
        if (action === FilePickerAction.Share) {
            this.props.userChoiceSetter(AttachOrSharePreference.Share);
        } else if (action === FilePickerAction.Attach) {
            this.props.userChoiceSetter(AttachOrSharePreference.Attach);
        }
    }
}

/**
 * The showFilePickerActionsPanel is responsible for creating an element and then rendering the FilePickerActionsPanel in it.
 * The reason to do that is to make this component simpler from a consumer perspective so that
 * they don't have to worry about creating the component themselves and maintaining show/close lifecycle.
 * Instead they create the panel and once the action is selected and the panel is dismissed they will get a callback
 * containing the user choice.
 */
let panelDiv = null;

export default function showFilePickerActionsPanel(
    resolve: (action: FilePickerAction) => void,
    reject: () => void,
    viewState: FilePickerActionsPanelViewState,
    supportedActions: FilePickerAction[],
    supportedActionsMessages: string[],
    rememberUserChoiceText: string,
    userChoiceSetter: (value: AttachOrSharePreference) => void,
    isMultipleSelection: boolean,
    currentProviderDisplayString: string,
    currentProviderType: FileProviderType,
    filesSource: SelectedFilesSource
): void {
    if (!panelDiv) {
        // As we can have only one panel open at a time so we only create the
        // DOM element once and reuse that.
        panelDiv = document.createElement('div');
        panelDiv.id = 'FilePickerActionsPanelView';
        document.body.appendChild(panelDiv);
    }

    ReactDOM.render(
        <FilePickerActionsPanelView
            onActionSelected={resolve}
            onCancelCallback={reject}
            viewState={viewState}
            supportedActions={supportedActions}
            supportedActionsMessages={supportedActionsMessages}
            rememberUserChoiceText={rememberUserChoiceText}
            userChoiceSetter={userChoiceSetter}
            isMultipleSelection={isMultipleSelection}
            currentProviderDisplayName={currentProviderDisplayString}
            currentProviderType={currentProviderType}
            filesSource={filesSource}
        />,
        panelDiv
    );

    filePickerActionsPanel.show(viewState);
}
